import { TFunction } from 'i18next';
import _get from 'lodash/get';
import * as Yup from 'yup';
import { WidgetsKindEnum } from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';

/**
 * Helper function to check if a specific widget is selected
 */
export function isWidgetSelected(
  values: Values,
  widgetType: WidgetsKindEnum,
): boolean {
  return _get(values, [WidgetFieldNames.Widget, 'data', 'kind']) === widgetType;
}

/**
 * Creates a conditional validation that only applies when a specific widget is selected
 */
export function getConditionalStringValidation(
  t: TFunction,
  widgetType: WidgetsKindEnum,
  isRequired = true,
) {
  return Yup.string().when(WidgetFieldNames.Widget, {
    is: (widget: SelectOption | undefined) =>
      _get(widget, 'data.kind') === widgetType,
    then: (schema) =>
      isRequired ? schema.required(t('required-field-error')) : schema,
    otherwise: (schema) => schema.notRequired(),
  });
}

/**
 * Creates a conditional URL validation that only applies when a specific widget is selected
 */
export function getConditionalUrlValidation(
  t: TFunction,
  widgetType: WidgetsKindEnum,
) {
  return Yup.string().when(WidgetFieldNames.Widget, {
    is: (widget: SelectOption | undefined) =>
      _get(widget, 'data.kind') === widgetType,
    then: (schema) =>
      schema.url(t('invalid-url-error')).required(t('required-field-error')),
    otherwise: (schema) => schema.notRequired(),
  });
}

/**
 * Creates a conditional select validation that only applies when a specific widget is selected
 */
export function getConditionalSelectValidation(
  t: TFunction,
  widgetType: WidgetsKindEnum,
) {
  return Yup.object()
    .shape({
      value: Yup.string(),
      label: Yup.string(),
    })
    .when(WidgetFieldNames.Widget, {
      is: (widget: SelectOption | undefined) =>
        _get(widget, 'data.kind') === widgetType,
      then: (schema) =>
        schema
          .required(t('required-field-error'))
          .test(
            'has-value',
            t('required-field-error'),
            (value) => !!value?.value,
          ),
      otherwise: (schema) => schema.notRequired().nullable(),
    });
}

/**
 * Creates a conditional location validation that only applies when a specific widget is selected
 */
export function getConditionalLocationValidation(
  t: TFunction,
  widgetType: WidgetsKindEnum,
) {
  return Yup.object()
    .shape({
      name: Yup.string(),
      lat: Yup.number(),
      lon: Yup.number(),
    })
    .nullable()
    .when(WidgetFieldNames.Widget, {
      is: (widget: SelectOption | undefined) =>
        _get(widget, 'data.kind') === widgetType,
      then: (schema) =>
        schema
          .required(t('required-field-error'))
          .test(
            'has-location',
            t('required-field-error'),
            (value) =>
              !!(
                value?.name &&
                value?.lat !== undefined &&
                value?.lon !== undefined
              ),
          ),
      otherwise: (schema) => schema.notRequired().nullable(),
    });
}
